import { useEffect, useMemo, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { Coins, Loader2, Plus, SlidersHorizontal } from 'lucide-react';
import { EditActionBar } from '../shared/EditActionBar';
import { ExportXlsxButton } from '../shared/ExportXlsxButton';
import type { Driver11Data, Q4CommissionsRow } from '../../../api/apiTypesConductRisk';
import { useConductRiskDriver } from '../../../api/hooks/useConductRiskDriver';
import { fetchExportConductRiskDriverXlsx } from '../../../api/functionApi';

const numCls = 'w-24 text-right bg-white text-[#1B365D] border border-amber-300 rounded px-1.5 py-0.5 text-xs focus:outline-none focus:border-amber-500 tabular-nums';
const txtCls = 'w-full min-w-[6rem] bg-white border border-amber-300 rounded px-1.5 py-0.5 text-xs focus:outline-none focus:border-amber-500';

/* ─── Helpers ────────────────────────────────────────────── */
const fmtE = (n: number) =>
  new Intl.NumberFormat('it-IT', { maximumFractionDigits: 2 }).format(n) + ' €';

const FASCIA_STYLES: Record<string, string> = {
  'NO PREMIO': 'bg-gray-100 text-gray-600 border-gray-200',
  'A PREMIO':  'bg-emerald-50 text-emerald-700 border-emerald-200',
};

const FasciaBadge = ({ fascia }: { fascia: string }) => (
  <span className={`inline-flex px-2 py-0.5 rounded-full text-[10px] font-bold border ${FASCIA_STYLES[fascia] ?? FASCIA_STYLES['NO PREMIO']}`}>
    {fascia}
  </span>
);

type TabKey = 'flagged' | 'all';

const emptyQ4CommissionsRow = (): Q4CommissionsRow => ({
  branch_name:                 '',
  clone_uo:                    '',
  area:                        '',
  regional_division:           '',
  net_commissions_q3:          0,
  fascia_q3:                   '',
  net_commissions_q4:          0,
  fascia_q4:                   '',
  q4_net_commissions_increase: 0,
  quarterly_mean:              0,
  delta_vs_mean:               0,
});

/* ─── Main ───────────────────────────────────────────────── */
const IncrementoCommissioniNetteContainer = ({ reportId }: { reportId: string }) => {
  const { data: remoteData, loading, error, save, isReadOnly } = useConductRiskDriver<Driver11Data>(reportId, 11);

  const [data,       setData]       = useState<Driver11Data | null>(null);
  const [savedData,  setSavedData]  = useState<Driver11Data | null>(null);
  const [soglia,     setSoglia]     = useState<number>(50);
  const [autoSaving, setAutoSaving] = useState(false);
  const [isEditing,  setIsEditing]  = useState(false);
  const [tab,        setTab]        = useState<TabKey>('flagged');
  const debounceRef    = useRef<ReturnType<typeof setTimeout> | null>(null);
  const initializedRef = useRef(false);

  const handleAnnulla = () => { if (savedData) setData(savedData); setIsEditing(false); };
  const handleSalva   = async () => {
    if (!data) return;
    await save(data);
    setSavedData(data);
    setIsEditing(false);
  };

  /** Aggiorna un campo NUMERICO della riga (per clone_uo) sia in source_rows
   *  che in flagged_rows. Editing libero: nessun ricalcolo lato client. */
  const updateRow = (cloneUo: string, key: keyof Q4CommissionsRow, raw: string) => {
    const val = parseFloat(raw);
    if (isNaN(val)) return;
    setData(prev => {
      if (!prev) return prev;
      const patch = (rows: Q4CommissionsRow[]) => rows.map(r => r.clone_uo === cloneUo ? { ...r, [key]: val } : r);
      return { ...prev, source_rows: patch(prev.source_rows), flagged_rows: patch(prev.flagged_rows) };
    });
  };

  /** Aggiorna un campo TESTUALE della riga (per clone_uo). Il matcher usa il
   *  valore corrente della chiave, così anche l'edit del clone_uo stesso resta
   *  coerente keystroke per keystroke. */
  const updateRowText = (cloneUo: string, key: keyof Q4CommissionsRow, value: string) => {
    setData(prev => {
      if (!prev) return prev;
      const patch = (rows: Q4CommissionsRow[]) => rows.map(r => r.clone_uo === cloneUo ? { ...r, [key]: value } : r);
      return { ...prev, source_rows: patch(prev.source_rows), flagged_rows: patch(prev.flagged_rows) };
    });
  };

  /** Aggiunge una riga vuota alla tabella del tab corrente. */
  const addRow = () => {
    setData(prev => {
      if (!prev) return prev;
      return tab === 'flagged'
        ? { ...prev, flagged_rows: [...prev.flagged_rows, emptyQ4CommissionsRow()] }
        : { ...prev, source_rows: [...prev.source_rows, emptyQ4CommissionsRow()] };
    });
  };

  useEffect(() => {
    if (!remoteData) return;
    // Difensivo: se il BE restituisce array null, normalizzo a [].
    const normalized: Driver11Data = {
      ...remoteData,
      source_rows:  remoteData.source_rows  ?? [],
      flagged_rows: remoteData.flagged_rows ?? [],
    };
    setData(normalized);
    setSavedData(normalized);
    if (!initializedRef.current) {
      setSoglia(remoteData.thresholds.deviation_pct);
      initializedRef.current = true;
    }
  }, [remoteData]);

  // Autosave soglia (~600ms debounced). Attivo solo fuori editing (in edit
  // la soglia va salvata dal bottone Salva, non da un autosave in parallelo).
  useEffect(() => {
    if (!data || !savedData || isReadOnly || isEditing) return;
    if (soglia === savedData.thresholds.deviation_pct) return;

    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      const payload: Driver11Data = { ...data, thresholds: { deviation_pct: soglia } };
      setAutoSaving(true);
      try {
        await save(payload);
        setSavedData(payload);
      } catch (err) {
        console.error('Autosave soglia Driver 11 fallito:', err);
        setSoglia(savedData.thresholds.deviation_pct);
      } finally {
        setAutoSaving(false);
      }
    }, 600);

    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [soglia, savedData, isReadOnly, isEditing, data, save]);

  const rows: Q4CommissionsRow[] = useMemo(() => {
    if (!data) return [];
    const src = tab === 'flagged' ? data.flagged_rows : data.source_rows;
    return [...src].sort((a, b) => b.delta_vs_mean - a.delta_vs_mean);
  }, [data, tab]);

  if (loading && !data) {
    return (
      <div className="flex items-center justify-center py-16">
        <div className="w-12 h-12 border-[5px] border-gray-200 border-t-amber-500 rounded-full animate-spin" />
      </div>
    );
  }
  if (error && !data) {
    return (
      <div className="p-6">
        <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg px-4 py-3">{error}</div>
      </div>
    );
  }
  if (!data) {
    return <div className="p-6 text-sm text-gray-400">Nessun dato disponibile per questo driver.</div>;
  }

  return (
    <div className="p-6 space-y-5">

      {/* Banner Investigation + Edit bar */}
      <div className="flex items-start gap-3">
        <div className="flex-1 flex items-center gap-3 px-4 py-3 rounded-xl bg-amber-50/60 border border-amber-100">
          <Coins className="w-4 h-4 text-amber-700 shrink-0" />
          <p className="text-xs text-amber-900 leading-relaxed">
            <span className="font-semibold">Investigation</span> · Analisi delle filiali che mostrano aumenti anomali delle commissioni nette nell'ultimo trimestre passando da "NO PREMIO" ad "A PREMIO". La ratio sottostante tale investigation risiede nell'individuare possibili condotte commerciali aggressive poste in essere dalla Rete al fine di conseguire un incremento del livello premiale.
          </p>
        </div>
        <ExportXlsxButton
          fetchBlob={() => fetchExportConductRiskDriverXlsx(reportId, 'Driver11')}
          fileName={`Incremento_Commissioni_Nette_Q4_${data.period}`}
        />
        <EditActionBar
          isEditing={isEditing}
          onProcedi={() => setIsEditing(true)}
          onAnnulla={handleAnnulla}
          onSalva={handleSalva}
          disabled={isReadOnly}
        />
      </div>

      {/* Soglia */}
      <div className="flex items-center gap-4 p-4 bg-white rounded-xl border border-gray-100">
        <div className="w-8 h-8 rounded-lg bg-amber-50 flex items-center justify-center shrink-0">
          <SlidersHorizontal className="w-4 h-4 text-amber-700" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-xs font-semibold text-amber-800">Soglia scarto accettabile Q4</p>
          <p className="text-[10px] text-gray-400 mt-0.5">
            Filiali flaggate quando l'incremento del Q4 supera la media trimestrale (Q1-Q3) di oltre il valore impostato · default {data.thresholds.deviation_pct}%
          </p>
        </div>
        <div className="flex items-center gap-1.5 shrink-0">
          {autoSaving && <Loader2 className="w-3.5 h-3.5 text-amber-700/60 animate-spin" />}
          <input
            type="number"
            min={1}
            max={500}
            value={soglia}
            disabled={isReadOnly || isEditing}
            onChange={e => {
              const v = parseFloat(e.target.value);
              if (!isNaN(v)) setSoglia(Math.max(1, Math.min(500, v)));
            }}
            className="w-16 text-right text-sm font-bold text-amber-800 border border-gray-200 rounded-lg px-2 py-1.5 focus:outline-none focus:border-amber-400 disabled:opacity-50 disabled:cursor-not-allowed"
          />
          <span className="text-xs font-medium text-gray-400">%</span>
        </div>
      </div>

      {/* KPI strip */}
      <div className="grid grid-cols-3 gap-4">
        <div className="bg-white rounded-xl border border-gray-100 p-5 text-center">
          <div className="text-3xl font-bold text-amber-700">{data.flagged_rows.length}</div>
          <div className="text-[11px] text-gray-400 mt-1">Filiali flaggate</div>
        </div>
        <div className="bg-white rounded-xl border border-gray-100 p-5 text-center">
          <div className="text-3xl font-bold text-[#1B365D]">{data.source_rows.length}</div>
          <div className="text-[11px] text-gray-400 mt-1">Filiali analizzate</div>
        </div>
        <div className="bg-white rounded-xl border border-gray-100 p-5 text-center">
          <div className="text-3xl font-bold text-[#1B365D]">{soglia}%</div>
          <div className="text-[11px] text-gray-400 mt-1">Soglia applicata</div>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 p-1 bg-gray-100 rounded-xl">
        <button
          onClick={() => setTab('flagged')}
          className={`flex-1 py-2 px-3 rounded-lg text-xs font-semibold transition-all ${
            tab === 'flagged' ? 'bg-white text-amber-800 shadow-sm' : 'text-gray-500 hover:text-gray-700'
          }`}
        >
          Solo flaggate
          <span className="ml-2 inline-flex items-center justify-center min-w-[20px] px-1.5 py-0.5 rounded-full text-[10px] font-bold bg-amber-100 text-amber-800">
            {data.flagged_rows.length}
          </span>
        </button>
        <button
          onClick={() => setTab('all')}
          className={`flex-1 py-2 px-3 rounded-lg text-xs font-semibold transition-all ${
            tab === 'all' ? 'bg-white text-amber-800 shadow-sm' : 'text-gray-500 hover:text-gray-700'
          }`}
        >
          Tutte le analizzate
          <span className="ml-2 inline-flex items-center justify-center min-w-[20px] px-1.5 py-0.5 rounded-full text-[10px] font-bold bg-gray-200 text-gray-700">
            {data.source_rows.length}
          </span>
        </button>
      </div>

      {/* Table */}
      {rows.length === 0 ? (
        <div className="bg-white rounded-xl border border-gray-100 p-8 text-center text-sm text-gray-400">
          Nessuna filiale in questa categoria.
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="bg-gray-50 text-gray-400 uppercase text-[8px] tracking-wide">
                  <th className="text-left  px-4 py-2.5 font-semibold">CloneUo</th>
                  <th className="text-left  px-4 py-2.5 font-semibold">Filiale</th>
                  <th className="text-left  px-4 py-2.5 font-semibold">Area</th>
                  <th className="text-left  px-4 py-2.5 font-semibold">Dir. Regionale</th>
                  <th className="text-right px-4 py-2.5 font-semibold">Comm. Q3 (Q1+Q2+Q3)</th>
                  <th className="text-center px-4 py-2.5 font-semibold">Fascia Q3</th>
                  <th className="text-right px-4 py-2.5 font-semibold">Comm. Q4 cumulato</th>
                  <th className="text-center px-4 py-2.5 font-semibold">Fascia Q4</th>
                  <th className="text-right px-4 py-2.5 font-semibold text-amber-600">Incremento Q4</th>
                  <th className="text-right px-4 py-2.5 font-semibold">Media trim.</th>
                  <th className="text-right px-4 py-2.5 font-semibold text-amber-600">Delta vs Media</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {rows.map((row, i) => (
                  <motion.tr
                    key={i}
                    initial={{ opacity: 0, x: -4 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: i * 0.02 }}
                    className="hover:bg-amber-50/30 transition-colors"
                  >
                    <td className="px-4 py-3 font-mono text-[10px] text-gray-500 whitespace-nowrap">
                      {isEditing ? <input type="text" className={txtCls} value={row.clone_uo} onChange={e => updateRowText(row.clone_uo, 'clone_uo', e.target.value)} /> : row.clone_uo}
                    </td>
                    <td className="px-4 py-3 font-medium text-[#1B365D] whitespace-nowrap">
                      {isEditing ? <input type="text" className={txtCls} value={row.branch_name} onChange={e => updateRowText(row.clone_uo, 'branch_name', e.target.value)} /> : row.branch_name}
                    </td>
                    <td className="px-4 py-3 text-[10px] text-gray-500 whitespace-nowrap">
                      {isEditing ? <input type="text" className={txtCls} value={row.area} onChange={e => updateRowText(row.clone_uo, 'area', e.target.value)} /> : row.area}
                    </td>
                    <td className="px-4 py-3 text-[10px] text-gray-500 whitespace-nowrap">
                      {isEditing ? <input type="text" className={txtCls} value={row.regional_division} onChange={e => updateRowText(row.clone_uo, 'regional_division', e.target.value)} /> : row.regional_division}
                    </td>
                    <td className="px-4 py-3 text-right text-gray-600 tabular-nums whitespace-nowrap">
                      {isEditing
                        ? <input type="number" step="0.01" className={numCls} value={row.net_commissions_q3} onChange={e => updateRow(row.clone_uo, 'net_commissions_q3', e.target.value)} />
                        : fmtE(row.net_commissions_q3)}
                    </td>
                    <td className="px-4 py-3 text-center">
                      {isEditing ? <input type="text" className={txtCls} value={row.fascia_q3} onChange={e => updateRowText(row.clone_uo, 'fascia_q3', e.target.value)} /> : <FasciaBadge fascia={row.fascia_q3} />}
                    </td>
                    <td className="px-4 py-3 text-right text-gray-700 font-semibold tabular-nums whitespace-nowrap">
                      {isEditing
                        ? <input type="number" step="0.01" className={numCls} value={row.net_commissions_q4} onChange={e => updateRow(row.clone_uo, 'net_commissions_q4', e.target.value)} />
                        : fmtE(row.net_commissions_q4)}
                    </td>
                    <td className="px-4 py-3 text-center">
                      {isEditing ? <input type="text" className={txtCls} value={row.fascia_q4} onChange={e => updateRowText(row.clone_uo, 'fascia_q4', e.target.value)} /> : <FasciaBadge fascia={row.fascia_q4} />}
                    </td>
                    <td className="px-4 py-3 text-right text-amber-700 font-bold tabular-nums whitespace-nowrap">
                      {isEditing
                        ? <input type="number" step="0.01" className={numCls} value={row.q4_net_commissions_increase} onChange={e => updateRow(row.clone_uo, 'q4_net_commissions_increase', e.target.value)} />
                        : fmtE(row.q4_net_commissions_increase)}
                    </td>
                    <td className="px-4 py-3 text-right text-gray-500 tabular-nums whitespace-nowrap">
                      {isEditing
                        ? <input type="number" step="0.01" className={numCls} value={row.quarterly_mean} onChange={e => updateRow(row.clone_uo, 'quarterly_mean', e.target.value)} />
                        : fmtE(row.quarterly_mean)}
                    </td>
                    <td className="px-4 py-3 text-right whitespace-nowrap">
                      {isEditing
                        ? <input type="number" step="0.01" className={numCls} value={row.delta_vs_mean} onChange={e => updateRow(row.clone_uo, 'delta_vs_mean', e.target.value)} />
                        : <span className={`inline-flex px-2 py-0.5 rounded-full font-bold text-[10px] border ${
                            row.delta_vs_mean > row.quarterly_mean * (soglia / 100)
                              ? 'bg-amber-50 border-amber-200 text-amber-700'
                              : 'bg-gray-50 border-gray-100 text-gray-500'
                          }`}>
                            {fmtE(row.delta_vs_mean)}
                          </span>}
                    </td>
                  </motion.tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {isEditing && (
        <div className="flex justify-end mt-2">
          <button
            type="button"
            onClick={() => addRow()}
            className="inline-flex items-center gap-1.5 text-xs font-medium text-[#1B365D] border border-[#1B365D]/30 hover:bg-[#1B365D]/5 rounded-lg px-3 py-1.5 transition-colors"
          >
            <Plus className="w-3.5 h-3.5" /> Aggiungi nuovo
          </button>
        </div>
      )}
    </div>
  );
};

export default IncrementoCommissioniNetteContainer;
